import { useEffect, useMemo } from 'react';
import { useStore } from '@nanostores/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  CodeBlock,
  Divider,
  HStack,
  Heading,
  NumberInput,
  ProgressBar,
  SegmentedControl,
  SegmentedControlItem,
  Switch,
  Text,
  TextArea,
  TextInput,
  VStack,
} from '@astryxdesign/core';

import { ForecastChart } from '../components/ForecastChart';
import { BASE, formatNumber, formatQuota, type ForecastItem } from '../lib/api';
import { $auth } from '../stores/auth';
import { $keys, loadKeys } from '../stores/keys';
import {
  $pgError,
  $pgHorizon,
  $pgInputSeries,
  $pgKey,
  $pgMode,
  $pgQuantiles,
  $pgResult,
  $pgResultMode,
  $pgRunning,
  $pgSample,
  $pgSeriesText,
  SAMPLES,
  loadSample,
  parseSeries,
  runForecast,
  type PlaygroundMode,
} from '../stores/playground';
import { usePageEvent } from '../analytics/usePageEvent';

export function PlaygroundPage() {
  usePageEvent('playground_opened');
  const auth = useStore($auth);
  const keys = useStore($keys);
  const mode = useStore($pgMode);
  const key = useStore($pgKey);
  const sample = useStore($pgSample);
  const seriesText = useStore($pgSeriesText);
  const horizon = useStore($pgHorizon);
  const quantiles = useStore($pgQuantiles);
  const running = useStore($pgRunning);
  const result = useStore($pgResult);
  const resultMode = useStore($pgResultMode);
  const error = useStore($pgError);
  const inputSeries = useStore($pgInputSeries);

  useEffect(() => {
    void loadKeys();
  }, []);

  const parsed = useMemo(() => parseSeries(seriesText), [seriesText]);
  const plan = auth.plan;
  const activeKeys = keys.filter((k) => !k.revoked);

  const first = result?.forecasts[0];
  const apiOrigin = BASE || window.location.origin;
  const snippet = useMemo(
    () => buildSnippet(apiOrigin, mode, parsed.values, horizon, quantiles),
    [apiOrigin, mode, parsed.values, horizon, quantiles],
  );

  const blocked = parsed.error !== null || (mode === 'key' && key.trim() === '');

  return (
    <VStack gap={3}>
      <div className="pg-head">
        <div className="pg-head__intro">
          <VStack gap={0.5}>
            <HStack gap={1} vAlign="center" wrap="wrap">
              <Heading level={1}>Playground</Heading>
              <Badge label="Live · metered" variant="green" />
            </HStack>
            <Text color="secondary">
              Real forecasts from the real model. Every run spends one call from your monthly quota,
              exactly like production traffic.
            </Text>
          </VStack>
        </div>

        {plan && (
          <Card padding={2} variant="blue" width={230}>
            <VStack gap={0.5}>
              <Text size="xsm" color="secondary">
                {plan.name} plan
              </Text>
              <Heading level={3}>{formatQuota(plan.monthly_quota)}</Heading>
              <Text size="xsm" color="secondary">
                calls / month · {plan.rate_limit_rpm} per minute
              </Text>
            </VStack>
          </Card>
        )}
      </div>

      <div className="pg-grid">
        {/* ---------- controls ---------- */}
        <VStack gap={2}>
          <Card padding={3} variant="default">
            <VStack gap={2}>
              <VStack gap={1}>
                <Text size="sm" weight="semibold">
                  Authentication
                </Text>
                <SegmentedControl
                  label="How to authenticate the call"
                  value={mode}
                  onChange={(v) => $pgMode.set(v as PlaygroundMode)}
                  layout="fill"
                >
                  <SegmentedControlItem value="session" label="This session" />
                  <SegmentedControlItem value="key" label="An API key" />
                </SegmentedControl>
                <Text size="xsm" color="secondary">
                  {mode === 'session' ? (
                    <>
                      Sends the login you are already holding to{' '}
                      <span className="mono">POST /api/playground/forecast</span>. No key needed —
                      the charge lands on this account.
                    </>
                  ) : (
                    <>
                      Sends a key to <span className="mono">POST /v1/forecast</span>, the byte-for-byte
                      request your integration makes.
                    </>
                  )}
                </Text>
              </VStack>

              {mode === 'key' && (
                <TextInput
                  label="API key"
                  type="password"
                  value={key}
                  onChange={(v) => $pgKey.set(v)}
                  placeholder="vct_live_…"
                  description="Kept in memory for this page only — never stored."
                  width="100%"
                />
              )}

              {mode === 'key' && activeKeys.length === 0 && (
                <Banner
                  status="info"
                  title="No active key on this account"
                  description="Create one, or switch back to session auth to run without a key."
                  endContent={<Button label="Create a key" variant="primary" size="sm" href="/app/keys" />}
                />
              )}

              <Divider variant="subtle" />

              <VStack gap={1}>
                <Text size="sm" weight="semibold">
                  Sample series
                </Text>
                <div className="pg-samples">
                  {SAMPLES.map((s) => (
                    <Button
                      key={s.id}
                      label={s.label}
                      variant={s.id === sample ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={() => loadSample(s.id)}
                    />
                  ))}
                </div>
                <Text size="xsm" color="secondary">
                  {SAMPLES.find((s) => s.id === sample)?.hint ?? 'Or paste your own numbers below.'}
                </Text>
              </VStack>

              <TextArea
                label="Observations"
                value={seriesText}
                onChange={(v) => $pgSeriesText.set(v)}
                description="Oldest first, evenly spaced. Commas, spaces or newlines all work."
                rows={7}
                width="100%"
                status={
                  parsed.error && seriesText.trim().length > 0
                    ? { type: 'error', message: parsed.error }
                    : {
                        type: 'success',
                        message: `${parsed.values.length} observations${plan ? ` of ${formatNumber(plan.max_context)} allowed` : ''}`,
                      }
                }
              />

              <HStack gap={2} wrap="wrap" vAlign="end">
                <NumberInput
                  label="Horizon"
                  value={horizon}
                  onChange={(v) => $pgHorizon.set(v ?? 1)}
                  min={1}
                  max={plan?.max_horizon ?? 64}
                  step={1}
                  description={plan ? `Max ${plan.max_horizon} on ${plan.name}` : undefined}
                  width={180}
                />
                <Switch
                  label="Quantile bands"
                  description={plan && !plan.quantiles ? 'Not on your plan' : 'P10–P90 uncertainty'}
                  value={quantiles}
                  onChange={(v) => $pgQuantiles.set(v)}
                  isDisabled={plan ? !plan.quantiles : false}
                />
              </HStack>

              <Button
                label={running ? 'Forecasting…' : 'Run forecast'}
                variant="primary"
                isLoading={running}
                isDisabled={blocked}
                onClick={() => void runForecast()}
                width="100%"
              />
            </VStack>
          </Card>
        </VStack>

        {/* ---------- result ---------- */}
        <VStack gap={2}>
          {error && <Banner status="error" title="Request failed" description={error} />}

          {result && first ? (
            <>
              {result.warning && (
                <Banner status="warning" title="Stub mode" description={result.warning} />
              )}

              <Card padding={3} variant="default">
                <VStack gap={2}>
                  <HStack justify="between" vAlign="center" wrap="wrap">
                    <Text weight="semibold">Forecast</Text>
                    <HStack gap={1} vAlign="center" wrap="wrap">
                      <Badge
                        label={resultMode === 'session' ? 'via session' : 'via API key'}
                        variant={resultMode === 'session' ? 'purple' : 'cyan'}
                      />
                      <Badge label={result.model} variant="neutral" />
                      <Badge label={`${result.inference_ms} ms model`} variant="info" />
                    </HStack>
                  </HStack>

                  <ForecastChart
                    history={inputSeries}
                    point={first.point}
                    lower={first.quantiles?.q10}
                    upper={first.quantiles?.q90}
                    height={300}
                  />
                </VStack>
              </Card>

              <div className="pg-stats">
                <Meta label="Horizon" value={String(result.horizon)} />
                <Meta label="Total latency" value={`${result.latency_ms} ms`} />
                <Meta label="Calls this period" value={formatNumber(result.usage.requests_this_period)} />
                <Meta label="Points billed" value={formatNumber(result.usage.forecast_points)} />
              </div>

              {result.usage.monthly_quota > 0 && (
                <Card padding={3} variant="default">
                  <VStack gap={1.5}>
                    <HStack justify="between" vAlign="center" wrap="wrap">
                      <Text size="sm" weight="semibold">
                        Quota after this call
                      </Text>
                      <Badge
                        label={`${quotaPct(result.usage.requests_this_period, result.usage.monthly_quota)}% used`}
                        variant={
                          quotaPct(result.usage.requests_this_period, result.usage.monthly_quota) >= 90
                            ? 'error'
                            : 'neutral'
                        }
                      />
                    </HStack>
                    <ProgressBar
                      label="Monthly forecast calls"
                      isLabelHidden
                      value={Math.min(result.usage.requests_this_period, result.usage.monthly_quota)}
                      max={result.usage.monthly_quota}
                      variant={
                        quotaPct(result.usage.requests_this_period, result.usage.monthly_quota) >= 90
                          ? 'error'
                          : 'accent'
                      }
                    />
                    <Text size="sm" color="secondary">
                      {formatNumber(result.usage.requests_this_period)} of{' '}
                      {formatNumber(result.usage.monthly_quota)} calls on {result.usage.plan} ·{' '}
                      {formatNumber(
                        Math.max(result.usage.monthly_quota - result.usage.requests_this_period, 0),
                      )}{' '}
                      remaining
                    </Text>
                  </VStack>
                </Card>
              )}

              <Card padding={3} variant="default">
                <VStack gap={1.5}>
                  <HStack justify="between" vAlign="center" wrap="wrap">
                    <Text weight="semibold">Forecast values</Text>
                    <Text size="xsm" color="secondary">
                      <span className="mono">{result.request_id}</span>
                    </Text>
                  </HStack>
                  <CodeBlock
                    code={formatForecastJson(first)}
                    language="json"
                    hasCopyButton
                    width="100%"
                    container="card"
                  />
                </VStack>
              </Card>
            </>
          ) : (
            <div className="pg-empty">
              <VStack gap={1} hAlign="center">
                <Text weight="semibold">No forecast yet</Text>
                <Text size="sm" color="secondary" justify="center">
                  Pick a sample or paste your own series, then run it. The chart, the billed usage and
                  the raw response all land here.
                </Text>
              </VStack>
            </div>
          )}

          <Card padding={3} variant="muted">
            <VStack gap={1.5}>
              <Text weight="semibold">
                {mode === 'session' ? 'The same call with your session token' : 'The same call from your terminal'}
              </Text>
              <CodeBlock code={snippet} language="bash" hasCopyButton width="100%" container="card" />
            </VStack>
          </Card>
        </VStack>
      </div>
    </VStack>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <Card padding={2} variant="default">
      <VStack gap={0.5}>
        <Text size="xsm" color="secondary">
          {label}
        </Text>
        <Text size="lg" weight="semibold">
          <span className="mono">{value}</span>
        </Text>
      </VStack>
    </Card>
  );
}

function quotaPct(used: number, quota: number): number {
  if (quota <= 0) return 0;
  return Math.min(100, Math.round((used / quota) * 1000) / 10);
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/**
 * `JSON.stringify(…, 2)` puts every number on its own line, which turns a
 * 24-step forecast into 300 lines of noise. Keep each array on one line.
 */
function formatForecastJson(item: ForecastItem): string {
  const row = (label: string, values: number[]) =>
    `  "${label}": [${values.map(round3).join(', ')}]`;

  const lines: string[] = [row('point', item.point)];
  if (item.mean) lines.push(row('mean', item.mean));
  if (item.quantiles) {
    const inner = Object.entries(item.quantiles)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `    "${k}": [${v.map(round3).join(', ')}]`);
    lines.push(`  "quantiles": {\n${inner.join(',\n')}\n  }`);
  }
  return `{\n${lines.join(',\n')}\n}`;
}

/**
 * The snippet mirrors whichever surface the mode selected, so copy-paste
 * reproduces what the button just did rather than something adjacent to it.
 */
function buildSnippet(
  apiOrigin: string,
  mode: PlaygroundMode,
  values: number[],
  horizon: number,
  quantiles: boolean,
): string {
  // Truncate the echoed series so the snippet stays readable.
  const shown = values.slice(0, 24);
  const tail = values.length > shown.length ? `, … ${values.length - shown.length} more` : '';
  const body = `-d '{
    "series": [[${shown.map(round3).join(', ')}${tail}]],
    "horizon": ${horizon},
    "quantiles": ${quantiles}
  }'`;

  if (mode === 'session') {
    return `curl -sS ${apiOrigin}/api/playground/forecast \\
  -H "Authorization: Bearer $VECTIME_SESSION_JWT" \\
  -H "Content-Type: application/json" \\
  ${body}`;
  }
  return `curl -sS ${apiOrigin}/v1/forecast \\
  -H "Authorization: Bearer $VECTIME_API_KEY" \\
  -H "Content-Type: application/json" \\
  ${body}`;
}
